import {
    ComponentGeneralDetailInput,
    Exhibition,
    ExhibitionInput,
    Showroom,
    UploadFile,
} from "@/app/lib/generated/graphql-types";
import { notNull } from "@/app/lib/common/not-null";
import {
    mutableDetailFrom,
} from "@/app/features/shared/__to_be_integrated/mapper/general/detail/mapper";
import { getUploadFileId } from "@/app/lib/common/get-image-id";
import { isUploadFile } from "@/app/lib/common/image-uploader/image-descriptor";
import { PLACEHOLDER_IMAGE } from "@/app/types/common/constants";
import { ExhibitionDetailEditable } from "@/app/features/sites/exhibitions/types/types";

export function exhibitionDetailEditableFrom(exhibition: Exhibition): ExhibitionDetailEditable {
    return {
        id: exhibition.documentId,
        title: exhibition.title,
        subtitle: exhibition.subtitle || undefined,
        excerpt: exhibition.excerpt,
        promoImage: exhibition.promoImage ?? PLACEHOLDER_IMAGE,
        flyerImage: exhibition.flyerImage || undefined,
        isHighlight: exhibition.isHighlight,
        order: exhibition.order || 0,
        startDate: exhibition.startDate || undefined,
        endDate: exhibition.endDate || undefined,
        details: exhibition.details?.filter(notNull).map(mutableDetailFrom),
        showroom: exhibition.showroom ? showroomEditableFrom(exhibition.showroom) : undefined,
        relatedInfos: (exhibition.relatedInfos?.filter(notNull) || []) as ExhibitionDetailEditable["relatedInfos"],
    };
}

function showroomEditableFrom(showroom: Showroom): NonNullable<ExhibitionDetailEditable["showroom"]> {
    return {
        id: showroom.documentId,
        title: showroom.title,
        subtitle: showroom.subtitle || undefined,
        description: showroom.description || undefined,
        images: showroom.images?.filter(notNull) || [],
    };
}

export async function exhibitionInputFromEditable(
    exhibition: ExhibitionDetailEditable
): Promise<ExhibitionInput> {
    if (!isUploadFile(exhibition.promoImage)) {
        throw new Error("PromoImage is not an UploadFile. Please provide only already uploaded files to this function");
    }

    if (exhibition.flyerImage && !isUploadFile(exhibition.flyerImage)) {
        throw new Error("FlyerImage is not an UploadFile. Please provide only already uploaded files to this function");
    }

    const promoImageId = await getUploadFileId(exhibition.promoImage as UploadFile);
    const flyerImageId = exhibition.flyerImage
        ? `${await getUploadFileId(exhibition.flyerImage as UploadFile)}`
        : undefined;

    const details: ComponentGeneralDetailInput[] = [];
    for (const detail of exhibition.details?.filter(notNull) || []) {
        let image: string | null = null;
        if (detail.image) {
            if (!isUploadFile(detail.image)) {
                throw new Error("Detail image is not an UploadFile. Please provide only already uploaded files to this function");
            }
            image = `${await getUploadFileId(detail.image)}`;
        }

        details.push({
            id: detail.id,
            viewVariant: detail.viewVariant,
            title: detail.title,
            subtitle: detail.subtitle,
            description: detail.description,
            image,
            imagePosition: detail.imagePosition,
            splitInitial: detail.splitInitial,
            splitMax: detail.splitMax,
            links: detail.links,
            documents: await Promise.all((detail.documents ?? []).filter(notNull).map(async document => ({
                id: document.id ?? undefined,
                order: document.order,
                media: document.media ? await getUploadFileId(document.media as UploadFile) : null,
            }))),
        });
    }

    return {
        title: exhibition.title,
        subtitle: exhibition.subtitle || null,
        excerpt: exhibition.excerpt,
        promoImage: `${promoImageId}`,
        flyerImage: flyerImageId,
        isHighlight: exhibition.isHighlight,
        order: exhibition.order,
        startDate: exhibition.startDate || null,
        endDate: exhibition.endDate || null,
        details,
    };
}
